import { useEffect, useState } from "react";
import { Trophy, Loader2, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/components/Layout";
import { GameCard } from "@/components/GameCard";

export default function Bracket() {
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});
  const [tieValue, setTieValue] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [po, pk] = await Promise.all([api.get("/playoffs"), api.get("/picks")]);
    setData(po.data);
    const map = {};
    pk.data.forEach((p) => { map[p.game_id] = p; });
    setPicks(map);
    const sb = po.data.rounds.find((r) => r.round === "superbowl")?.games?.[0];
    if (sb && map[sb.id]?.predicted_total != null) setTieValue(String(map[sb.id].predicted_total));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handlePick = async (gameId, team) => {
    const prev = picks[gameId];
    setPicks((s) => ({ ...s, [gameId]: { ...(s[gameId] || {}), game_id: gameId, selected_team: team } }));
    try {
      await api.post("/picks", { game_id: gameId, selected_team: team });
    } catch (err) {
      setPicks((s) => ({ ...s, [gameId]: prev }));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sbGame = data?.rounds.find((r) => r.round === "superbowl")?.games?.[0];
  const saveTie = async () => {
    if (!sbGame || tieValue === "") return;
    try {
      await api.post("/picks/tiebreaker", { game_id: sbGame.id, predicted_total: parseInt(tieValue, 10) });
      toast.success("Super Bowl tiebreaker saved!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <PageShell testid="bracket-page">
      <div className="mb-8">
        <span className="overline">Automated Postseason · Combined Champion Race</span>
        <h1 className="font-display text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <Trophy className="h-8 w-8 neon-text" /> Playoff Bracket
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 neon-text animate-spin" /></div>
      ) : !data?.active ? (
        <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-12 text-center" data-testid="bracket-inactive">
          <Sparkles className="h-8 w-8 neon-text mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold">Playoffs haven't started yet</h2>
          <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">
            The bracket unlocks automatically after Week 18. Points scale up each round:
            Wild Card ×2, Divisional ×4, Conference ×6, Super Bowl ×10.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start" data-testid="bracket-rounds">
            {data.rounds.map((rnd) => (
              <div key={rnd.round} data-testid={`round-${rnd.round}`} className="space-y-4">
                <div className="flex items-center justify-between sticky top-16 bg-[#050505]/80 backdrop-blur py-2 z-10">
                  <h2 className="font-display text-lg font-bold">{rnd.label}</h2>
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#D50A0A]/10 neon-text">
                    {rnd.multiplier} pts
                  </span>
                </div>
                {rnd.games.map((g, i) => (
                  <GameCard key={g.id} game={g} pick={picks[g.id]} onPick={handlePick} index={i} />
                ))}
              </div>
            ))}
          </div>

          {sbGame && (
            <div className="mt-8 rounded-2xl border border-[#D50A0A]/30 bg-[#D50A0A]/5 p-6" data-testid="sb-tiebreaker-panel">
              <span className="overline">Super Bowl Tiebreaker</span>
              <h2 className="font-display text-xl font-bold mt-2">Predict the combined final score</h2>
              <p className="text-zinc-400 text-sm mt-1">{sbGame.away} @ {sbGame.home} — total points scored by both teams. Breaks the season tie.</p>
              <div className="flex items-center gap-3 mt-4">
                <input data-testid="sb-tiebreaker-input" type="number" value={tieValue} disabled={sbGame.locked}
                  onChange={(e) => setTieValue(e.target.value)}
                  className="w-32 rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] disabled:opacity-50" placeholder="e.g. 48" />
                <button data-testid="sb-tiebreaker-save-btn" onClick={saveTie} disabled={sbGame.locked || tieValue === ""}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50">
                  <Save className="h-4 w-4" /> Save
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}
